import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import toast from 'react-hot-toast';
import { FaPlay, FaBook, FaPen, FaVolumeUp, FaLanguage, FaArrowLeft, FaLock, FaStar, FaCheck, FaCrown } from 'react-icons/fa';
import ChallengeMode from '../../components/ChallengeMode';
import Leaderboard from '../../components/Leaderboard';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: 'english' | 'spanish' | 'french';
  song_lyrics: string;
  translation: string | null;
  song_vocabulary: any;
  song_likes: number;
  is_free: boolean;
}

interface PracticeStats {
  total_exercises: number;
  completed_exercises: number;
  accuracy_rate: number;
  favorite_exercise: string;
}

export default function PracticeHub() {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [practiceStats, setPracticeStats] = useState<PracticeStats | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [processingSubscription, setProcessingSubscription] = useState(false);
  const [hasFreeTrialUsed, setHasFreeTrialUsed] = useState(false);
  const [showChallengeMode, setShowChallengeMode] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = getCurrentUser();

  useEffect(() => {
    loadSongs();
    checkAccess();
    loadPracticeStats();
    checkFreeTrialStatus();
    
    // Check if we should activate a specific tab from navigation
    const state = location.state as any;
    if (state?.activeTab === 'challenge') {
      setShowChallengeMode(true);
      setShowLeaderboard(false);
    } else if (state?.activeTab === 'leaderboard') {
      setShowChallengeMode(false);
      setShowLeaderboard(true);
    }
  }, [selectedLanguage, location.state]);

  const checkAccess = async () => {
    if (!user) return;

    try {
      if (user.is_admin) {
        setHasAccess(true);
        return;
      }

      // Check for valid coupon usage
      const { data: couponUsage, error: couponError } = await supabase
        .from('stripeencouponusage')
        .select('*')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString());

      if (couponError && couponError.code !== 'PGRST116') {
        throw couponError;
      }

      // Check for practice subscription
      const { data: practiceSubscription, error: subscriptionError } = await supabase
        .from('stripepracticesubscriptions')
        .select('*')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString())
        .maybeSingle();

      if (subscriptionError) {
        throw subscriptionError;
      }

      setHasAccess(
        (!!couponUsage && couponUsage.length > 0) || 
        !!practiceSubscription
      );
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    }
  };

  const checkFreeTrialStatus = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('stripepracticesubscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_free_trial', true);

      if (error) throw error;
      
      setHasFreeTrialUsed(data && data.length > 0);
    } catch (error) {
      console.error('Error checking free trial status:', error);
    }
  };

  const loadSongs = async () => {
    try {
      let query = supabase
        .from('stripeensongs')
        .select('*')
        .eq('is_visible', true);

      if (selectedLanguage !== 'all') {
        query = query.eq('language', selectedLanguage);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setSongs(data || []);
    } catch (error: any) {
      toast.error('Error loading songs');
    } finally {
      setLoading(false);
    }
  };

  const loadPracticeStats = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('stripepracticestats')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false }); // returns array

      if (error) { console.error(error); }
      const statsArray = data || [];
      if (statsArray.length > 0) {
        setPracticeStats(statsArray[0]);
      } else {
        setPracticeStats(null);
      }
    } catch (error) {
      console.error('Error loading practice stats:', error);
    }
  };

  const handleSongSelect = (song: Song) => {
    if (!user) {
      toast.error('Please log in to access practice mode');
      navigate('/login');
      return;
    }

    if (!hasAccess && !song.is_free) {
      setShowSubscriptionModal(true);
      return;
    }

    navigate(`/practice/song/${song.id}`);
  };

  const handleJoinChallenge = (sessionId: string, songId: string, expiresAt: string, rewardPoints: number) => {
    navigate(`/practice/song/${songId}`, {
      state: {
        challengeMode: true,
        sessionId,
        expiresAt,
        rewardPoints
      }
    });
  };

  const handleCreateChallenge = (songId: string) => {
    // Check if user is admin before allowing challenge creation
    if (!user?.is_admin) {
      toast.error('Solo los administradores pueden crear desafíos');
      return;
    }

    navigate(`/practice/song/${songId}`, {
      state: {
        createChallenge: true
      }
    });
  };

  const handleSubscribe = async (isFreeTrial: boolean = false) => {
    if (!user) {
      toast.error('Please log in to subscribe');
      navigate('/login');
      return;
    }

    setProcessingSubscription(true);
    try {
      // Get user's current balance if not free trial
      if (!isFreeTrial) {
        const { data: userData } = await supabase
          .from('stripeusers')
          .select('wallet_balance')
          .eq('id', user.id)
          .single();

        if (!userData) throw new Error('User not found');
        if (userData.wallet_balance < 12) {
          toast.error('Insufficient balance. Please add funds to your wallet.');
          navigate('/wallet');
          return;
        }
      }

      // Calculate subscription period
      const currentDate = new Date();
      const validUntil = new Date(currentDate);
      
      if (isFreeTrial) {
        validUntil.setDate(validUntil.getDate() + 1); // 1 day for free trial
      } else {
        validUntil.setDate(validUntil.getDate() + 30); // 30 days for paid subscription
      }

      // Create subscription record
      const { error: subscriptionError } = await supabase
        .from('stripepracticesubscriptions')
        .insert({
          user_id: user.id,
          valid_until: validUntil.toISOString(),
          is_free_trial: isFreeTrial,
          amount: isFreeTrial ? 0 : 12
        });

      if (subscriptionError) throw subscriptionError;

      // If not free trial, update user's balance
      if (!isFreeTrial) {
        const { data: userData } = await supabase
          .from('stripeusers')
          .select('wallet_balance')
          .eq('id', user.id)
          .single();

        if (!userData) throw new Error('User not found');

        // Update user's balance
        const { error: updateError } = await supabase
          .from('stripeusers')
          .update({ 
            wallet_balance: userData.wallet_balance - 12 
          })
          .eq('id', user.id);

        if (updateError) throw updateError;

        // Create withdraw transaction
        const { error: transactionError } = await supabase
          .from('stripetransactions')
          .insert({
            user_id: user.id,
            amount: 12,
            type: 'withdraw',
            status: 'completed',
            description: 'Practice Mode Subscription'
          });

        if (transactionError) throw transactionError;

        // Update local storage with new balance
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const userObj = JSON.parse(userJson);
          userObj.wallet_balance = userData.wallet_balance - 12;
          localStorage.setItem('user', JSON.stringify(userObj));
        }
      }

      toast.success(isFreeTrial 
        ? '¡Free trial activated! Enjoy 1 day of full access.' 
        : 'Subscription successful! You now have 30 days of full access.');
      
      setHasAccess(true);
      setShowSubscriptionModal(false);
      
      if (isFreeTrial) {
        setHasFreeTrialUsed(true);
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to process subscription');
    } finally {
      setProcessingSubscription(false);
    }
  };

  const exerciseTypes = [
    {
      id: 'fill-blanks',
      title: 'Fill in the Blanks',
      description: 'Complete missing words in song lyrics',
      icon: <FaPen className="w-6 h-6" />,
      color: 'bg-blue-500',
      difficulty: 'Easy'
    },
    {
      id: 'listen-write',
      title: 'Listen & Write',
      description: 'Listen to lyrics and write what you hear',
      icon: <FaVolumeUp className="w-6 h-6" />,
      color: 'bg-green-500',
      difficulty: 'Medium'
    },
    {
      id: 'verb-conjugation',
      title: 'Verb Conjugation',
      description: 'Practice conjugating verbs from the lyrics',
      icon: <FaLanguage className="w-6 h-6" />,
      color: 'bg-purple-500',
      difficulty: 'Hard'
    },
    {
      id: 'vocabulary',
      title: 'Vocabulary Quiz',
      description: 'Test your knowledge of words from the song',
      icon: <FaBook className="w-6 h-6" />,
      color: 'bg-orange-500',
      difficulty: 'Medium'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link
              to="/dashboard"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <FaArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Practice Mode</h1>
            <p className="mt-2 text-gray-600">
              Improve your language skills with interactive exercises
            </p>
          </div>
        </div>

        {/* Practice Stats */}
        {practiceStats && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Progress</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{practiceStats.total_exercises}</div>
                <div className="text-sm text-gray-600">Total Exercises</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{practiceStats.completed_exercises}</div>
                <div className="text-sm text-gray-600">Completed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{practiceStats.accuracy_rate}%</div>
                <div className="text-sm text-gray-600">Accuracy</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  <FaStar className="w-6 h-6 mx-auto" />
                </div>
                <div className="text-sm text-gray-600">{practiceStats.favorite_exercise}</div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Banner */}
        {!hasAccess && (
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="p-6 md:flex md:items-center md:justify-between">
              <div className="mb-4 md:mb-0 md:flex-1">
                <div className="flex items-center mb-2">
                  <FaCrown className="w-6 h-6 text-black mr-2" />
                  <h2 className="text-xl font-bold text-black">Premium Practice Mode</h2>
                </div>
                <p className="text-black/80">
                  Unlock all practice exercises and premium features for just $12/month
                </p>
                <ul className="mt-3 space-y-1">
                  <li className="flex items-center text-black/80 text-sm">
                    <FaCheck className="w-4 h-4 mr-2 text-black" />
                    Unlimited access to all practice exercises
                  </li>
                  <li className="flex items-center text-black/80 text-sm">
                    <FaCheck className="w-4 h-4 mr-2 text-black" />
                    AI-powered personalized feedback
                  </li>
                  <li className="flex items-center text-black/80 text-sm">
                    <FaCheck className="w-4 h-4 mr-2 text-black" />
                    Track your progress and improve faster
                  </li>
                </ul>
              </div>
              <div className="flex flex-col space-y-2 md:space-y-3">
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors duration-200 font-semibold"
                >
                  Subscribe Now
                </button>
                {!hasFreeTrialUsed && (
                  <button
                    onClick={() => handleSubscribe(true)}
                    disabled={processingSubscription}
                    className="px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors duration-200 font-semibold"
                  >
                    {processingSubscription ? 'Processing...' : 'Try 1 Day Free'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Challenge Mode and Leaderboard Tabs */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => {
                  setShowChallengeMode(false);
                  setShowLeaderboard(false);
                }}
                className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                  !showChallengeMode && !showLeaderboard
                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Práctica Individual
              </button>
              <button
                onClick={() => {
                  setShowChallengeMode(true);
                  setShowLeaderboard(false);
                }}
                className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                  showChallengeMode
                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Modo Desafío
              </button>
              <button
                onClick={() => {
                  setShowChallengeMode(false);
                  setShowLeaderboard(true);
                }}
                className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                  showLeaderboard
                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Clasificación
              </button>
            </nav>
          </div>

          <div className="p-6">
            {showChallengeMode ? (
              <ChallengeMode
                songs={songs}
                hasAccess={hasAccess}
                onShowSubscriptionModal={() => setShowSubscriptionModal(true)}
                onJoinChallenge={handleJoinChallenge}
                onCreateChallenge={handleCreateChallenge}
              />
            ) : showLeaderboard ? (
              <Leaderboard limit={20} />
            ) : (
              <div className="text-center py-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Práctica Individual</h3>
                <p className="text-gray-600">Selecciona una canción abajo para comenzar a practicar</p>
              </div>
            )}
          </div>
        </div>

        {/* Exercise Types */}
        <div className={`bg-white rounded-xl shadow-sm p-6 mb-8 ${showChallengeMode || showLeaderboard ? 'hidden' : ''}`}>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Exercise Types</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {exerciseTypes.map((exercise) => (
              <div
                key={exercise.id}
                className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow duration-200"
              >
                <div className={`w-12 h-12 ${exercise.color} rounded-lg flex items-center justify-center text-white mb-3`}>
                  {exercise.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{exercise.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{exercise.description}</p>
                <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                  exercise.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                  exercise.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {exercise.difficulty}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Language Filter */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Filter by Language</h2>
          <div className="flex flex-wrap gap-2">
            {['all', 'english', 'spanish', 'french'].map((lang) => (
              <button
                key={lang}
                onClick={() => setSelectedLanguage(lang)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  selectedLanguage === lang
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {lang.charAt(0).toUpperCase() + lang.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Access Status */}
        {!user?.is_admin && (
          <div className="mb-8 p-4 rounded-lg bg-black">
            <div className="flex items-center">
              <div className={`rounded-full p-2 mr-3 ${hasAccess ? 'bg-yellow-500' : 'bg-yellow-500'}`}>
                {hasAccess ? (
                  <FaPlay className="w-5 h-5 text-black" />
                ) : (
                  <FaLock className="w-5 h-5 text-black" />
                )}
              </div>
              <div>
                <h3 className={`font-semibold ${hasAccess ? 'text-yellow-400' : 'text-yellow-400'}`}>
                  {hasAccess ? 'Full Practice Access' : 'Limited Practice Access'}
                </h3>
                <p className={`text-sm ${hasAccess ? 'text-white' : 'text-white'}`}>
                  {hasAccess
                    ? 'Practice with all songs and exercises!'
                    : 'Subscribe to practice with all premium songs.'}
                </p>
              </div>
              {!hasAccess && (
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="ml-auto bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors duration-200 font-semibold"
                >
                  Subscribe
                </button>
              )}
            </div>
          </div>
        )}

        {/* Songs Selection */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Choose a Song to Practice</h2>
            <p className="text-gray-600 mt-1">Select any song to start practicing with different exercise types</p>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array(6).fill(0).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-300 h-48 rounded-lg mb-4"></div>
                    <div className="bg-gray-300 h-4 rounded mb-2"></div>
                    <div className="bg-gray-300 h-4 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : songs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {songs.map((song) => (
                  <div
                    key={song.id}
                    onClick={() => handleSongSelect(song)}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="relative">
                      <img
                        src={song.song_cover}
                        alt={song.creator}
                        className="w-full h-48 object-cover"
                      />
                      {!hasAccess && !song.is_free && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <FaLock className="w-8 h-8 text-white" />
                        </div>
                      )}
                      {song.is_free && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                          Free
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{song.creator}</h3>
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">
                          {song.language}
                        </span>
                        <span className="text-sm text-gray-500">{song.song_likes} likes</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <FaBook className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Songs Available</h3>
                <p className="text-gray-600">
                  {selectedLanguage !== 'all' 
                    ? `No songs available in ${selectedLanguage}. Try selecting a different language.`
                    : 'No songs are currently available for practice.'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaCrown className="text-yellow-400 w-5 h-5 mr-2" />
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Premium Practice</h3>
                </div>
                <button
                  onClick={() => setShowSubscriptionModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="bg-yellow-50 rounded-lg p-4 mb-4 sm:mb-6 border border-yellow-200">
                <p className="text-gray-700 mb-4">
                  Unlock full access to all practice exercises and premium features for 30 days.
                </p>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Subscription Price:</span>
                  <span className="font-bold text-gray-900">$12.00</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium text-gray-900">30 days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Payment Method:</span>
                  <span className="font-medium text-gray-900">Wallet Balance</span>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                <h4 className="font-semibold text-gray-900">What You Get:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <FaCheck className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Unlimited access to all practice exercises</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">AI-powered personalized feedback on your progress</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Advanced verb conjugation and vocabulary exercises</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Premium AI voice for listening exercises</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => handleSubscribe(false)}
                  disabled={processingSubscription}
                  className="w-full px-6 py-3 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingSubscription ? 'Processing...' : 'Subscribe for $12'}
                </button>
                
                {!hasFreeTrialUsed && (
                  <button
                    onClick={() => handleSubscribe(true)}
                    disabled={processingSubscription}
                    className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingSubscription ? 'Processing...' : 'Start 1-Day Free Trial'}
                  </button>
                )}
                
                <button
                  onClick={() => setShowSubscriptionModal(false)}
                  className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}